import { Cookies } from "react-cookie";
import defaultAxios, { AxiosError } from "axios";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { tokenKey } from "../constants/common";
import {
  AuthState,
  CheckToken,
  DefaultResponse,
  LoginBody,
  LoginResponse,
  User,
} from "../types";
import axios from "../services/axios";
import { useQueryClient } from "react-query";
import Swal from "sweetalert2";
import { getDomainWithoutSubdomain } from "../utils/getDomainWithoutSubdomain";

interface IAuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

const AuthProvider = ({ children }: IAuthProviderProps) => {
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
  const [isPreparingApp, setPreparingApp] = useState<boolean>(true);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User>();
  const cookies = useMemo(() => new Cookies(), []);
  const queryClient = useQueryClient();

  const handleRemoveToken = useCallback(async () => {
    try {
      if (process.env.REACT_APP_NODE_ENV === "production") {
        const domainUrl = getDomainWithoutSubdomain(window.location.href);
        await cookies.remove(tokenKey, {
          path: "/",
          domain: `.${domainUrl}`,
        });
      } else {
        await cookies.remove(tokenKey);
      }
    } catch (error) {
      throw error;
    }
  }, [cookies]);

  const handleErrorResponse = useCallback((error: unknown) => {
    if (defaultAxios.isAxiosError(error)) {
      const serverError = error as AxiosError<any>;
      if (serverError && serverError.response) {
        console.log(`serverError`, serverError.response);
        if (serverError.response!.status === 400) {
          Swal.fire({
            title: "Terjadi Kesalahan!",
            text: `${serverError.response?.data?.data}`,
            icon: "error",
            confirmButtonColor: "#45A779",
            customClass: {
              container: "my-swal",
            },
          });
        }

        if (serverError.response!.status === 422) {
          Swal.fire({
            title: "Terjadi Kesalahan!",
            text: `Ada error validasi`,
            icon: "error",
            confirmButtonColor: "#45A779",
            customClass: {
              container: "my-swal",
            },
          });
          console.log("", `${serverError.response.data?.message}`, [
            { text: "OK" },
          ]);
        }

        if (serverError.response!.status === 403) {
          Swal.fire({
            title: "Terjadi Kesalahan!",
            text: `${serverError.response.data?.message}`,
            icon: "error",
            confirmButtonColor: "#45A779",
            customClass: {
              container: "my-swal",
            },
          });
          console.log("", `${serverError.response.data.data}`, [
            { text: "OK" },
          ]);
        }
      } else {
        console.error("", `Terjadi kesalahan! Silahkan coba lagi.`, [
          { text: "OK" },
        ]);
      }
    }
  }, []);

  const checkToken = useCallback(
    async (token: string) => {
      try {
        const { data } = await axios.post<CheckToken>(`/api/cektoken`, {
          token: token,
        });

        if (token && data.code === 200) {
          const { data: waAdmin } = await axios.get<
            DefaultResponse<{
              nomor_wa: string | null;
            }>
          >("/api/wa-marketing", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          setUser({ ...data.data, nomor_wa: waAdmin.data.nomor_wa });

          setAuthenticated(true);
          setLoading(false);
        }
        setLoading(false);
      } catch (error) {
        handleErrorResponse(error);
        setAuthenticated(false);
        handleRemoveToken();
        queryClient.clear();
      }
    },
    [handleErrorResponse, handleRemoveToken, queryClient],
  );

  const loadResourcesAndDataAsync = useCallback(async () => {
    try {
      const token = await cookies.get(tokenKey);
      if (token) {
        await checkToken(token);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setPreparingApp(false);
    }
  }, [checkToken, cookies]);

  useEffect(() => {
    loadResourcesAndDataAsync();
  }, [loadResourcesAndDataAsync]);

  const handleSetToken = useCallback(
    async (token: string | null) => {
      try {
        if (process.env.REACT_APP_NODE_ENV === "production") {
          const domainUrl = getDomainWithoutSubdomain(window.location.href);
          cookies.set(tokenKey, token, {
            path: "/",
            domain: `.${domainUrl}`,
          });
        } else {
          await cookies.set(tokenKey, token, {
            path: "/",
          });
        }
      } catch (error) {
        throw error;
      }
    },
    [cookies],
  );

  const login = useCallback(
    async (values: LoginBody) => {
      setLoading(true);
      try {
        const { data } = await axios.post<LoginResponse>("/api/mlogin", {
          username: values.email,
          password: values.password,
          coload_id: 2,
        });
        console.log(`data`, data);

        if (data.code === 200) {
          const { data: waAdmin } = await axios.get<
            DefaultResponse<{
              nomor_wa: string | null;
            }>
          >("/api/wa-marketing", {
            headers: {
              Authorization: `Bearer ${data.data.access_token}`,
            },
          });

          setUser({ ...data.data, nomor_wa: waAdmin.data.nomor_wa });

          setAuthenticated(true);
          handleSetToken(data.data.access_token);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleErrorResponse(error);
      }
    },
    [handleErrorResponse, handleSetToken],
  );

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<DefaultResponse<null>>("/api/mlogout");
      if (data.code === 200) {
        setAuthenticated(false);
        handleRemoveToken();
        queryClient.clear();
      }
      setLoading(false);
    } catch (error) {
      handleErrorResponse(error);
      setLoading(false);
    }
  }, [handleErrorResponse, handleRemoveToken, queryClient]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      checkToken,
      login,
      user,
      logout,
    }),
    [isAuthenticated, isLoading, checkToken, login, user, logout],
  );

  if (isPreparingApp) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};

export { AuthContext, AuthProvider, useAuth };
